import React, { useEffect, useState } from 'react';
import { loadClubBranding } from '../clubBranding';
import wingitDesign from '../designSystem';

export default function ClubAppearancePanel({
  organisationId,
  appSettings,
  logoUploadError,
  clubHeroFiles,
  clubAppearanceStatus,
  handleLogoFileSelected,
  handleRemoveLogo,
  handleClubHeroFilesSelected,
  handleSaveClubAppearance,
}) {
  const ds = wingitDesign;
  const [savedBranding, setSavedBranding] = useState({
    logoUrl: '',
    heroImages: [],
  });

  const [heroPreviews, setHeroPreviews] = useState([]);

  useEffect(() => {
    let cancelled = false;

    async function fetchSavedBranding() {
      const branding =
        await loadClubBranding(
          organisationId
        );

      if (!cancelled) {
        setSavedBranding({
          logoUrl: branding.logoUrl || '',
          heroImages: Array.isArray(branding.heroImages) ? branding.heroImages : [],
        });
      }
    }

    fetchSavedBranding();

    return () => {
      cancelled = true;
    };
  }, [organisationId, clubAppearanceStatus]);

  useEffect(() => {
    const urls = clubHeroFiles.map((file) => (file ? URL.createObjectURL(file) : ''));

    const merged = [0, 1, 2, 3, 4].map((index) => (
      urls[index] || savedBranding.heroImages[index] || ''
    ));

    setHeroPreviews(merged);

    return () => {
      urls.forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [clubHeroFiles, savedBranding.heroImages]);

  const logoPreview = appSettings?.branding?.companyLogoDataUrl || savedBranding.logoUrl || '';
  const brandingMessageTone = getMessageTone(
    logoUploadError || clubAppearanceStatus
  );

  return (
    <section style={panelWrapStyle(ds)}>
      <div style={headerRowStyle}>
        <h3 style={titleStyle(ds)}>Club branding</h3>
        <p style={subtitleStyle(ds)}>
          Keep your logo and dashboard images tidy and consistent.
        </p>
      </div>

      <div style={cardStyle(ds)}>
        <div style={brandingGridStyle}>
          <div style={sectionCardStyle(ds)}>
            <div style={sectionHeaderStyle}>
              <div style={sectionTitleStyle(ds)}>Logo</div>
              <p style={sectionDescriptionStyle(ds)}>
                Used across owner tools and invoice previews.
              </p>
            </div>

            <label style={logoTileStyle(ds)}>
              <input type="file" accept="image/*" onChange={handleLogoFileSelected} style={{ display: 'none' }} />

              {logoPreview ? (
                <img src={logoPreview} alt="Club logo preview" style={logoPreviewStyle} />
              ) : (
                <>
                  <div style={tileLabelStyle(ds)}>Club logo</div>
                  <div style={plusStyle(ds)}>+</div>
                  <div style={hintStyle(ds)}>Select an image to upload</div>
                </>
              )}
            </label>

            <div style={actionRowStyle}>
              <label style={secondaryActionLabelStyle(ds)}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoFileSelected}
                  style={{ display: 'none' }}
                />
                {logoPreview ? 'Replace logo' : 'Upload logo'}
              </label>

              {logoPreview ? (
                <button type="button" onClick={handleRemoveLogo} style={dangerButtonStyle(ds)}>
                  Remove logo
                </button>
              ) : null}
            </div>

            {!logoPreview ? (
              <div style={supportTextStyle(ds)}>
                No club logo uploaded yet.
              </div>
            ) : null}

            {logoUploadError ? (
              <div style={feedbackStyle(ds, 'danger')}>
                {logoUploadError}
              </div>
            ) : null}
          </div>

          <div style={sectionCardStyle(ds)}>
            <div style={sectionHeaderStyle}>
              <div style={sectionTitleStyle(ds)}>Dashboard images</div>
              <p style={sectionDescriptionStyle(ds)}>
                Upload up to five images for the dashboard carousel.
              </p>
            </div>

            <div style={galleryGridStyle}>
              {[0, 1, 2, 3, 4].map((index) => {
                const preview = heroPreviews[index];

                return (
                  <label key={index} style={galleryTileStyle(ds)}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleClubHeroFilesSelected(e, index)}
                      style={{ display: 'none' }}
                    />

                    {preview ? (
                      <img src={preview} alt={`Club preview ${index + 1}`} style={galleryImageStyle} />
                    ) : (
                      <>
                        <div style={galleryPlusStyle(ds)}>+</div>
                        <div style={galleryHintStyle(ds)}>Image {index + 1}</div>
                      </>
                    )}
                  </label>
                );
              })}
            </div>

            {clubAppearanceStatus ? (
              <div style={feedbackStyle(ds, brandingMessageTone)}>
                {clubAppearanceStatus}
              </div>
            ) : null}

            <div style={actionRowStyle}>
              <button type="button" onClick={handleSaveClubAppearance} style={primaryButtonStyle(ds)}>
                Save club branding
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function getMessageTone(message) {
  const value = String(message || '').trim().toLowerCase();

  if (!value) {
    return 'info';
  }

  if (
    value.includes('could not') ||
    value.includes('failed') ||
    value.includes('please select') ||
    value.includes('too large') ||
    value.includes('error')
  ) {
    return 'danger';
  }

  if (
    value.includes('click save') ||
    value.includes('selected') ||
    value.includes('optimised')
  ) {
    return 'warning';
  }

  if (value.includes('saved')) {
    return 'success';
  }

  return 'info';
}

function panelWrapStyle(ds) {
  return {
    marginTop: '1.5rem',
    marginBottom: '1.5rem',
  };
}

const headerRowStyle = {
  marginBottom: '0.75rem',
};

function titleStyle(ds) {
  return {
    color: ds.colors.text,
    fontSize: ds.typography.sectionTitle.fontSize,
    fontWeight: ds.typography.sectionTitle.fontWeight,
    letterSpacing: ds.typography.sectionTitle.letterSpacing,
    margin: 0,
  };
}

function subtitleStyle(ds) {
  return {
    color: ds.colors.textMuted,
    margin: '0.2rem 0 0',
    fontSize: '0.88rem',
    lineHeight: 1.45,
    maxWidth: 560,
  };
}

function cardStyle(ds) {
  return {
    ...ds.cards.secondary,
    borderRadius: ds.radius.large,
    padding: '1rem',
  };
}

const brandingGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: '1rem',
};

function sectionCardStyle(ds) {
  return {
    ...ds.cards.section,
    marginBottom: 0,
    display: 'grid',
    gap: '0.8rem',
    minWidth: 0,
  };
}

const sectionHeaderStyle = {
  display: 'grid',
  gap: '0.3rem',
};

function sectionTitleStyle(ds) {
  return {
    color: ds.colors.text,
    fontSize: '0.95rem',
    fontWeight: 700,
    lineHeight: 1.2,
  };
}

function sectionDescriptionStyle(ds) {
  return {
    color: ds.colors.textMuted,
    margin: 0,
    fontSize: '0.82rem',
    lineHeight: 1.45,
  };
}

function logoTileStyle(ds) {
  return {
    minHeight: 122,
    border: `1px dashed ${ds.colors.borderStrong}`,
    borderRadius: ds.radius.medium,
    background:
      'linear-gradient(135deg, rgba(17, 28, 46, 0.96), rgba(12, 22, 36, 0.96))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    cursor: 'pointer',
    overflow: 'hidden',
    padding: '0.9rem',
    textAlign: 'center',
  };
}

const logoPreviewStyle = {
  maxHeight: 72,
  maxWidth: '100%',
  objectFit: 'contain',
  display: 'block',
};

function tileLabelStyle(ds) {
  return {
    color: ds.colors.text,
    fontWeight: 700,
    fontSize: '0.9rem',
    marginBottom: 8,
  };
}

function plusStyle(ds) {
  return {
    color: ds.colors.primary,
    fontSize: '2rem',
    lineHeight: 1,
    marginBottom: 6,
  };
}

function hintStyle(ds) {
  return {
    color: ds.colors.textMuted,
    fontSize: '0.8rem',
    lineHeight: 1.35,
  };
}

const actionRowStyle = {
  display: 'flex',
  gap: '0.55rem',
  flexWrap: 'wrap',
  alignItems: 'center',
};

function secondaryActionLabelStyle(ds) {
  return {
    ...ds.buttons.secondary,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 36,
    padding: '0.5rem 0.82rem',
    fontSize: '0.82rem',
    fontWeight: 650,
    lineHeight: 1,
    borderRadius: ds.radius.medium,
    cursor: 'pointer',
    boxSizing: 'border-box',
  };
}

function dangerButtonStyle(ds) {
  return {
    ...ds.buttons.secondary,
    minHeight: 36,
    padding: '0.5rem 0.82rem',
    fontSize: '0.82rem',
    fontWeight: 650,
    lineHeight: 1,
    borderRadius: ds.radius.medium,
    background: ds.colors.dangerSoft,
    color: '#ffc0ca',
    border: `1px solid ${ds.colors.danger}`,
    cursor: 'pointer',
  };
}

function supportTextStyle(ds) {
  return {
    color: ds.colors.textMuted,
    fontSize: '0.8rem',
    lineHeight: 1.4,
  };
}

const galleryGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))',
  gap: '0.7rem',
};

function galleryTileStyle(ds) {
  return {
    minHeight: 92,
    border: `1px dashed ${ds.colors.borderStrong}`,
    borderRadius: ds.radius.medium,
    background:
      'linear-gradient(135deg, rgba(17, 28, 46, 0.96), rgba(12, 22, 36, 0.96))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    cursor: 'pointer',
    textAlign: 'center',
    overflow: 'hidden',
  };
}

const galleryImageStyle = {
  width: '100%',
  height: 92,
  objectFit: 'cover',
  display: 'block',
};

function galleryPlusStyle(ds) {
  return {
    color: ds.colors.primary,
    fontSize: '1.55rem',
    lineHeight: 1,
    marginBottom: 6,
  };
}

function galleryHintStyle(ds) {
  return {
    color: ds.colors.textMuted,
    fontSize: '0.75rem',
    lineHeight: 1.3,
  };
}

function feedbackStyle(ds, tone) {
  const chip =
    tone === 'danger'
      ? ds.chips.danger
      : tone === 'warning'
        ? ds.chips.warning
        : tone === 'success'
          ? ds.chips.success
          : ds.chips.info;

  return {
    ...chip,
    borderRadius: ds.radius.medium,
    padding: '0.65rem 0.75rem',
    fontSize: '0.82rem',
    lineHeight: 1.4,
  };
}

function primaryButtonStyle(ds) {
  return {
    ...ds.buttons.primary,
    minHeight: 36,
    padding: '0.5rem 0.82rem',
    fontSize: '0.82rem',
    fontWeight: 650,
    lineHeight: 1,
    borderRadius: ds.radius.medium,
    boxSizing: 'border-box',
  };
}